<script setup lang="ts">
/**
 * BG §22.4 — the blinking status indicator. Opacity fades 1 → 0.35 and back on a 2.2s loop, which is
 * deliberately slow: BG §22.3 forbids rapid flashing, and anything under ~2s starts to read as an alarm
 * rather than a heartbeat.
 *
 * The dot is decorative. Colour alone must never carry meaning (BG §22.3), so every caller pairs it with
 * a text label and the dot itself is `aria-hidden`.
 */
import { computed } from 'vue'
import { type ExecutionState, STATE_COLOUR } from '../tokens'

const props = withDefaults(
    defineProps<{
        /** Which BG §18.4 state colour the dot takes. */
        state?: ExecutionState
        /** Static dots are for legends and inactive rows, where a pulse would be noise. */
        pulse?: boolean
    }>(),
    { state: 'active', pulse: true },
)

const colour = computed(() => STATE_COLOUR[props.state])
</script>

<template>
    <span
        class="zh-status-dot"
        :data-pulse="pulse ? 'on' : 'off'"
        :style="{ '--dot-colour': colour }"
        aria-hidden="true"
    />
</template>

<style scoped>
.zh-status-dot {
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--dot-colour);
    box-shadow: 0 0 0.5rem color-mix(in srgb, var(--dot-colour) 70%, transparent);
    vertical-align: baseline;
}

.zh-status-dot[data-pulse='on'] {
    animation: zh-status-blink var(--motion-status-blink) ease-in-out infinite;
}

@keyframes zh-status-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.35;
    }
}

/* BG §22.2 — a live system, but never at the cost of someone who asked for stillness. */
@media (prefers-reduced-motion: reduce) {
    .zh-status-dot[data-pulse='on'] {
        animation: none;
        opacity: 1;
    }
}
</style>
